// Core
import { useState } from 'react';
import { useQuery } from 'react-query';
import { Box, Button, Flex, Text, Image, useColorMode } from '@chakra-ui/react';

// Assets
import { apiService } from 'services/ApiService';
import buttomPlayImage from 'assets/img/details/buttom-play.png';
import placeholderVideo from 'assets/img/video-play.png';

// Components
import { ArrowPaginateIcon } from 'components/Icons/Icons';

const ExclusiveContent = () => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const [activeIndex, setActiveIndex] = useState(0);

  const { data = [] } = useQuery({
    queryKey: ['banner-home-page-exclusive'],
    queryFn: async () => {
      const response = await apiService.getBanners({ type: 'exclusive' });
      return response.data || [];
    },
    refetchOnWindowFocus: false,
    retry: false,
  });

  const activeSlide = data[activeIndex];

  const itemWidth = 225;
  const gap = 8;
  const itemsPerScroll = 1;

  const maxVisibleItems = 3;

  let translateX = 0;

  if (activeIndex > 1 && activeIndex <= data.length) {
	translateX = (activeIndex - 1) * (itemWidth + gap);
  }

  const maxTranslate =
	(data.length - maxVisibleItems) * (itemWidth + gap);

  translateX = Math.min(translateX, maxTranslate);

  const nextSlide = () => {
    setActiveIndex((prev) => Math.min(prev + 1, data.length - 1));
  };

  const prevSlide = () => {
    setActiveIndex((prev) => Math.max(prev - 1, 0));
  };

  if (!data.length || !activeSlide) {
    return null;
  }

  return (
    <>
      <Text
        as="h4"
        variant="header"
        fontSize={{ base: '24px', md: '32px' }}
        fontStyle="normal"
        mt={{ base: '60px', sm: '73px' }}
        mb={{ base: '24px', sm: '32px' }}
      >
        Exclusive Content
      </Text>

      <Box
        padding="0px"
        borderRadius="16px"
        maxW="904px"
        position="relative"
        m={{ base: '0px auto 80px auto', sm: '0px auto 80px auto' }}
      >
        <Box
          borderRadius="16px"
          overflow="hidden"
          position="relative"
          w="100%"
          maxH="508px"
          h="100%"
          aspectRatio="16/9"
          cursor="pointer"
          onClick={() =>
            window.open(activeSlide?.objectUrl, '_blank', 'noopener,noreferrer')
          }
        >
          <Image
            src={activeSlide?.image || placeholderVideo}
            alt={activeSlide?.title || 'Exclusive Content'}
            w="100%"
            h="100%"
            objectFit="cover"
          />

          <Image
            src={buttomPlayImage}
            position="absolute"
            top="50%"
            cursor="pointer"
            left="50%"
            w="135px"
            transform="translate(-50%, -50%)"
          />
        </Box>

        {data.length > 1 && (
          <Flex mt="12px" alignItems="center" gap="12px" w="100%" position="relative">
            <Button
              variant="button-lg"
              display={{ base: 'none', md: 'flex' }}
              w="64px"
              borderRadius="full"
              disabled={activeIndex === 0}
              onClick={prevSlide}
              flexShrink={0}
            >
              <ArrowPaginateIcon transform="rotate(180deg)" fontSize="24px" />
            </Button>

            <Box
              bg={
                isDark
                  ? 'linear-gradient(269deg, rgba(5, 10, 25, 0.00) 0.79%, #050A19 99.22%)'
                  : 'linear-gradient(269deg, rgba(224, 234, 255, 0.00) 0.79%, #E0EAFF 99.22%)'
              }
              w={{ base: '10px', md: '50px' }}
              h="80px"
              position="absolute"
              left={{ base: '0px', md: '75px' }}
              zIndex="2"
              pointerEvents="none"
              opacity={{ base: '1', md: activeIndex >= 1 ? 1 : 0}}
              transition="0.2s all"
            />

            <Flex
			  gap="8px"
			  padding={{ base: '0 10px', md: '0' }}
			  overflowX={{ base: 'auto', md: 'hidden' }}
			  overflowY="hidden"
			  flex="1"
			  sx={{
			    scrollbarWidth: 'none',
			    msOverflowStyle: 'none',
			    '&::-webkit-scrollbar': {
			      display: 'none',
			    },
			  }}
			>
			  <Flex
			    gap="8px"
			    transform={{ base: 'none', md: `translateX(-${translateX}px)` }}
			    transition={{ base: 'none', md: 'transform 0.3s ease' }}
			  >
                {data.map((item, index) => {
                  const isActive = index === activeIndex;

                  return (
                    <Flex
                      key={item.id || index}
                      onClick={() => setActiveIndex(index)}
                      cursor="pointer"
                      flex="0 0 auto"
                      minW={`${itemWidth}px`}
                      maxW={`${itemWidth}px`}
                      padding="12px 12px 12px 12px"
                      alignItems="center"
                      gap="12px"
                      borderRadius="12px"
                      border={
                        isActive
                          ? '1px solid #2ABDFD'
                          : isDark
                            ? '1px solid #262D47'
                            : '1px solid #CBE7FF'
                      }
                      bg={
                        isActive
                          ? isDark
                            ? '#0D234D'
                            : '#EBF7FF'
                          : isDark
                            ? '#0E1633'
                            : '#FFF'
                      }
                      transition="0.2s all"
                    >
                      <Image
                        src={item.image || placeholderVideo}
                        alt={item.title}
                        w="100px"
                        h="54px"
                        objectFit="cover"
                        borderRadius="8px"
                      />

                      <Text
                        variant="title-md"
                        fontStyle="italic"
                        fontWeight="900"
                        noOfLines={1}
                      >
                        {item.title}
                      </Text>
                    </Flex>
                  );
                })}
              </Flex>
            </Flex>

            <Box
              bg={
                isDark
                  ? 'linear-gradient(90deg, rgba(5, 10, 25, 0.00) 0.79%, #050A19 99.22%)'
                  : 'linear-gradient(90deg, rgba(224, 234, 255, 0.00) 0.79%, #E0EAFF 99.22%)'
              }
              w={{ base: '20px', md: '50px' }}
              h="80px"
              position="absolute"
              right={{ base: '0px', md: '75px' }}
              position="absolute"
              zIndex="2"
              pointerEvents="none"
              opacity={{ base: '1', md: activeIndex < data.length - 1 ? 1 : 0}}
              transition="0.2s all"
            />

            <Button
              variant="button-lg"
              display={{ base: 'none', md: 'flex' }}
              w="64px"
              borderRadius="full"
              disabled={activeIndex === data.length - 1}
              onClick={nextSlide}
              flexShrink={0}
            >
              <ArrowPaginateIcon fontSize="24px" />
            </Button>
          </Flex>
        )}
      </Box>
    </>
  );
};

export default ExclusiveContent;